import { httpRequest } from "@/lib/http-client";

export interface AdminUser {
  /**
   * Present on `btl.user.v1.User` (field 4) and returned by admin ListUsers.
   * It was missing here, so `user?.bio` was always undefined and the profile
   * rendered "No bio available." for everyone.
   */
  bio?: string;
  id: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  tier?: string;
  isVerified?: boolean;
  createdAt?: string;
  bannedAt?: string | null;
  banReason?: string;
}

export interface PageInfo {
  nextPageToken?: string;
  previousPageToken?: string;
  totalCount?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

export interface ListUsersResponse {
  users: AdminUser[];
  pageInfo?: PageInfo;
}

export interface ListUsersParams {
  query?: string;
  pageSize?: number;
  page?: number;
  isVerified?: boolean | null;
  bannedOnly?: boolean;
}

export function listUsers({
  query,
  pageSize,
  page,
  isVerified,
  bannedOnly,
}: ListUsersParams): Promise<ListUsersResponse> {
  const params: Record<string, string | number | boolean | undefined> = {
    query: query ?? "",
    page_size: pageSize,
    page,
  };
  if (isVerified != null) {
    params.is_verified = isVerified;
  }
  if (bannedOnly) {
    params.banned_only = true;
  }
  return httpRequest<ListUsersResponse>("/v1/admin/users", { params });
}

export interface ListAdminsParams {
  query?: string;
  pageSize?: number;
  page?: number;
}

export function listAdmins({
  query,
  pageSize,
  page,
}: ListAdminsParams): Promise<ListUsersResponse> {
  return httpRequest<ListUsersResponse>("/v1/admin/admins", {
    params: {
      query: query ?? "",
      page_size: pageSize,
      page,
    },
  });
}

export interface GrantAdminParams {
  userId?: string;
  username?: string;
}

export interface GrantAdminResponse {
  success: boolean;
  userId?: string;
}

export function grantAdmin(params: GrantAdminParams): Promise<GrantAdminResponse> {
  return httpRequest<GrantAdminResponse>("/v1/admin/admins/grant", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface RevokeAdminParams {
  userId: string;
}

export function revokeAdmin(params: RevokeAdminParams): Promise<{ success: boolean }> {
  return httpRequest<{ success: boolean }>("/v1/admin/admins/revoke", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface BanUserParams {
  userId: string;
  reason: string;
}

export function banUser(params: BanUserParams): Promise<{ success: boolean }> {
  return httpRequest<{ success: boolean }>("/v1/admin/users/ban", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface SetUserVerifiedResponse {
  verified: boolean;
  changed: boolean;
}

/**
 * Set or clear a user's verification badge.
 *
 * Verification records that an account is a GENUINE AUTHOR. It is deliberately
 * not the authorized-publisher allowlist, which records a commercial/monetary
 * partner arrangement — both make content indexable, and conflating them would
 * file real writers as paid partners.
 *
 * `changed` is false when the user was already in that state, which is how a
 * caller tells a real action from a double-click.
 */
export function setUserVerified(
  userId: string,
  verified: boolean,
): Promise<SetUserVerifiedResponse> {
  return httpRequest<SetUserVerifiedResponse>(`/v1/admin/users/${userId}/verified`, {
    method: "POST",
    body: JSON.stringify({ verified }),
  });
}

export interface UnbanUserParams {
  userId: string;
}

export function unbanUser(params: UnbanUserParams): Promise<{ success: boolean }> {
  return httpRequest<{ success: boolean }>("/v1/admin/users/unban", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface ResetHandleParams {
  userId: string;
  handle: string;
  /**
   * Leave the old @handle redirecting to the new placeholder. Defaults to false
   * (the history is purged and the old handle 404s), because the reason to reset
   * a handle from here is that it impersonates something — and a redirect from
   * the impersonating handle to the offender's new one keeps the confusion alive.
   */
  keepRedirect?: boolean;
}

export function resetHandle(params: ResetHandleParams): Promise<{ success: boolean }> {
  return httpRequest<{ success: boolean }>("/v1/admin/users/reset-handle", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// Per-article removal of outbound-link rel tokens (protos v0.177.0). Each
// field is a REMOVAL: the zero value (all false/undefined) is the platform
// default — sponsored/ugc/nofollow are all kept as the renderer would already
// emit them. omitNofollow is the one that grants ranking equity to the linked
// site, so it is the only one the BFF requires a note for.
export interface LinkRelOverrides {
  omitSponsored?: boolean;
  omitUgc?: boolean;
  omitNofollow?: boolean;
}

export interface AdminContentItem {
  id?: string;
  title?: string;
  slug?: string;
  type?: string;
  status?: string;
  visibility?: string;
  // Platform moderation state, a SEPARATE axis from `visibility` (which the
  // author controls). Absent on older admin-service deploys, and unset is
  // equivalent to MODERATION_STATUS_NONE, so callers must treat both as
  // "visible" rather than as an unknown state.
  moderationStatus?: string;
  // Absent means every token is kept (the platform default) — see
  // LinkRelOverrides above.
  linkRelOverrides?: LinkRelOverrides;
  seoDescription?: string;
  seoImageUrl?: string;
  creator?: {
    id?: string;
    username?: string;
    displayName?: string;
    avatarUrl?: string;
    isVerified?: boolean;
  };
  authors?: Array<{
    id?: string;
    username?: string;
    displayName?: string;
    avatarUrl?: string;
  }>;
  canonicalRoute?: {
    path?: string;
    handle?: string;
  };
  viewCount?: number;
  likeCount?: number;
  thoughtCount?: number;
  bookmarkCount?: number;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface ListContentResponse {
  items: AdminContentItem[];
  pageInfo?: PageInfo;
}

export interface ListContentParams {
  query?: string;
  pageSize?: number;
  page?: number;
  type?: string;
  status?: string;
  visibility?: string;
  category?: string;
}

export function listContent({
  query,
  pageSize,
  page,
  type,
  status,
  visibility,
  category,
}: ListContentParams): Promise<ListContentResponse> {
  return httpRequest<ListContentResponse>("/v1/admin/content", {
    params: {
      query: query ?? "",
      page_size: pageSize,
      page,
      type,
      status,
      visibility,
      category,
      expand: "creator",
    },
  });
}

export function getContent(contentId: string): Promise<AdminContentItem> {
  return httpRequest<AdminContentItem>(`/v1/admin/content/${contentId}`);
}

export function getContentFlags(contentId: string): Promise<{ items: ContentFlagItem[] }> {
  return httpRequest<{ items: ContentFlagItem[] }>(`/v1/admin/content/${contentId}/flags`);
}

// ── Moderation ──────────────────────────────────────────────────────────────

export interface ContentFlagItem {
  id: string;
  contentId: string;
  flaggedById: string;
  reason: string;
  details: string;
  status: string;
  reviewedById?: string;
  createdAt?: string;
  reviewedAt?: string;
  contentTitle?: string;
  flaggedBy?: {
    id: string;
    username?: string;
    displayName?: string;
    avatarUrl?: string;
    isVerified?: boolean;
  };
}

export interface ListContentFlagsResponse {
  items: ContentFlagItem[];
  pageInfo?: PageInfo;
}

export interface ListContentFlagsParams {
  status?: string;
  reason?: string;
  // Free-text search over the flag's details and the flagged content's title,
  // applied server-side before paging. There is deliberately no thought
  // equivalent: thought-service's flag listing has no query field, so the queue
  // offers search on the content source only rather than a box that silently
  // does nothing on one of the two.
  query?: string;
  pageSize?: number;
  page?: number;
}

export function listContentFlags({
  status,
  reason,
  query,
  pageSize,
  page,
}: ListContentFlagsParams): Promise<ListContentFlagsResponse> {
  return httpRequest<ListContentFlagsResponse>("/v1/admin/moderation/flags", {
    params: {
      status,
      reason,
      query: query || undefined,
      page_size: pageSize,
      page,
    },
  });
}

// moderation_action enum, JSON (string) form over the BFF wire.
//
// HIDE and DERANK both produce the same visible outcome: off every public/
// discovery surface (trending/feeds/search), but the content STAYS on the
// author's own profile and its direct URL still 200s. REMOVE is the only
// action that 404s everywhere, including the author's own profile — reserve
// it for manual/severe cases. HIDE and DERANK are kept as distinct actions so
// audit logs/analytics can distinguish an explicit moderator call (HIDE) from
// the automated reach-control signal (DERANK, applied by the auto-detector),
// even though end users see no difference between them today.
export type ModerationActionValue =
  | "MODERATION_ACTION_DISMISS"
  | "MODERATION_ACTION_HIDE"
  | "MODERATION_ACTION_REMOVE"
  | "MODERATION_ACTION_DERANK";

export interface ReviewContentFlagParams {
  flagId: string;
  status: string;
  /**
   * OPTIONAL, because reviewing a flag and moderating the content are separate
   * decisions. content-service's `moderationStatusForAction` returns
   * `apply=false` for an unspecified action, so omitting this changes the flag's
   * state and deliberately leaves the content alone — which is exactly what the
   * content list's "Mark flags reviewed" and "Dismiss flags" buttons do.
   *
   * It was typed as required, which was wrong; the content list omits it on
   * purpose and only a non-functioning typecheck kept that from being an error.
   */
  moderationAction?: ModerationActionValue;
}

export interface ReviewContentFlagResponse {
  flag: ContentFlagItem;
}

export function reviewContentFlag({
  flagId,
  status,
  moderationAction,
}: ReviewContentFlagParams): Promise<ReviewContentFlagResponse> {
  return httpRequest<ReviewContentFlagResponse>(`/v1/admin/moderation/flags/${flagId}/review`, {
    method: "POST",
    body: JSON.stringify({ status, moderation_action: moderationAction }),
  });
}

// ── Thought flags ─────────────────────────────────────────────────────────

export interface ThoughtFlagItem {
  id: string;
  thoughtId: string;
  flaggedById: string;
  reason: string;
  details: string;
  status: string;
  reviewedById?: string;
  createdAt?: string;
  reviewedAt?: string;
  thoughtPreview?: string;
  flaggedBy?: {
    id: string;
    username?: string;
    displayName?: string;
    avatarUrl?: string;
    isVerified?: boolean;
  };
}

export interface ListThoughtFlagsResponse {
  items: ThoughtFlagItem[];
  pageInfo?: PageInfo;
}

export interface ListThoughtFlagsParams {
  status?: string;
  reason?: string;
  pageSize?: number;
  page?: number;
}

export function listThoughtFlags({
  status,
  reason,
  pageSize,
  page,
}: ListThoughtFlagsParams): Promise<ListThoughtFlagsResponse> {
  return httpRequest<ListThoughtFlagsResponse>("/v1/admin/moderation/thought-flags", {
    params: {
      status,
      reason,
      page_size: pageSize,
      page,
    },
  });
}

export interface ReviewThoughtFlagParams {
  flagId: string;
  status: string;
  moderationAction: ModerationActionValue;
}

export interface ReviewThoughtFlagResponse {
  flag: ThoughtFlagItem;
}

export function reviewThoughtFlag({
  flagId,
  status,
  moderationAction,
}: ReviewThoughtFlagParams): Promise<ReviewThoughtFlagResponse> {
  return httpRequest<ReviewThoughtFlagResponse>(
    `/v1/admin/moderation/thought-flags/${flagId}/review`,
    {
      method: "POST",
      body: JSON.stringify({ status, moderation_action: moderationAction }),
    },
  );
}

// ── Direct per-content moderation ─────────────────────────────────────────
// Applies one action to ONE content item. Unlike reviewContentFlag this needs no
// flag to exist first, and unlike cascadeHideUser it touches only this item.

export interface ModerateContentParams {
  contentId: string;
  moderationAction: ModerationActionValue;
  note?: string;
}

export interface ModerateContentResponse {
  contentId?: string;
  // The status now in force, and the one it replaced. `changed` is false when
  // the item was already in this status — the action is idempotent, and the
  // response says so rather than reporting a replay as a fresh takedown.
  moderationStatus?: string;
  previousModerationStatus?: string;
  changed?: boolean;
  auditFlag?: ContentFlagItem;
}

export function moderateContent({
  contentId,
  moderationAction,
  note,
}: ModerateContentParams): Promise<ModerateContentResponse> {
  return httpRequest<ModerateContentResponse>(`/v1/admin/moderation/content/${contentId}/action`, {
    method: "POST",
    body: JSON.stringify({ moderation_action: moderationAction, note: note ?? "" }),
  });
}

// ── Outbound link rel overrides ────────────────────────────────────────────
// Removes rel="sponsored"/"ugc"/"nofollow" from ONE article's outbound
// external links. admin-service requires `note` when omitNofollow is true —
// removing nofollow grants the link ranking equity, which must be a
// deliberate, justified admin act.

export interface SetContentLinkRelOverridesParams {
  contentId: string;
  omitSponsored: boolean;
  omitUgc: boolean;
  omitNofollow: boolean;
  note?: string;
}

export function setContentLinkRelOverrides({
  contentId,
  omitSponsored,
  omitUgc,
  omitNofollow,
  note,
}: SetContentLinkRelOverridesParams): Promise<AdminContentItem> {
  return httpRequest<AdminContentItem>(
    `/v1/admin/moderation/content/${contentId}/link-rel-overrides`,
    {
      method: "POST",
      body: JSON.stringify({
        omitSponsored,
        omitUgc,
        omitNofollow,
        note: note ?? "",
      }),
    },
  );
}

// ── Trusted sites (editorial link allowlist) ───────────────────────────────
// For @btl's own articles, a link to a domain on this list renders as a plain
// editorial citation. Every other external link renders as a paid placement.
// Matching is by domain suffix, so a listed domain also covers its
// subdomains: admin-service's own concern, not something this client needs
// to compute.

export interface TrustedSite {
  domain: string;
  note: string;
  addedById: string;
  createdAt: string;
  linkingArticleCount: number;
}

export interface ListTrustedSitesResponse {
  sites: TrustedSite[];
}

export function listTrustedSites(): Promise<ListTrustedSitesResponse> {
  return httpRequest<ListTrustedSitesResponse>("/v1/admin/trusted-sites");
}

export interface AddTrustedSiteParams {
  domain: string;
  note?: string;
}

export interface AddTrustedSiteResponse {
  site: TrustedSite;
}

export function addTrustedSite({
  domain,
  note,
}: AddTrustedSiteParams): Promise<AddTrustedSiteResponse> {
  return httpRequest<AddTrustedSiteResponse>("/v1/admin/trusted-sites", {
    method: "POST",
    body: JSON.stringify({ domain, note: note ?? "" }),
  });
}

export interface RemoveTrustedSiteParams {
  domain: string;
  note?: string;
}

export function removeTrustedSite({ domain, note }: RemoveTrustedSiteParams): Promise<void> {
  return httpRequest<void>(`/v1/admin/trusted-sites/${encodeURIComponent(domain)}`, {
    method: "DELETE",
    ...(note ? { body: JSON.stringify({ note }) } : {}),
  });
}

// ── Cascade hide ──────────────────────────────────────────────────────────

export interface CascadeHideUserResponse {
  contentAffected?: number;
  thoughtsAffected?: number;
}

export function cascadeHideUser(
  userId: string,
  moderationAction: ModerationActionValue = "MODERATION_ACTION_HIDE",
  note?: string,
): Promise<CascadeHideUserResponse> {
  return httpRequest<CascadeHideUserResponse>(`/v1/admin/moderation/users/${userId}/cascade-hide`, {
    method: "POST",
    body: JSON.stringify({ moderation_action: moderationAction, note: note ?? "" }),
  });
}

// ── New writers ──────────────────────────────────────────────────────────
// Creators whose first published article landed inside a recent window.
// Backed by content-service's ListNewWriters/ListNewWriterArticles
// (protos#224) through admin-service's hand-registered BFF.

export type NewWriterWindow = "7d" | "30d" | "90d";

export interface NewWriter {
  creatorId: string;
  handle?: string;
  displayName?: string;
  isVerified: boolean;
  accountCreatedAt?: string;
  firstPublishedAt?: string;
  articleCount: number;
  busiestDayCount: number;
  cleanCount: number;
  needsReviewCount: number;
  spamCount: number;
  moderatedCount: number;
  isAuthorizedPublisher: boolean;
}

export interface ListNewWritersResponse {
  writers: NewWriter[];
  pageInfo?: PageInfo;
}

export interface ListNewWritersParams {
  window: NewWriterWindow;
  pageSize?: number;
  page?: number;
}

export function listNewWriters({
  window,
  pageSize,
  page,
}: ListNewWritersParams): Promise<ListNewWritersResponse> {
  return httpRequest<ListNewWritersResponse>("/v1/admin/new-writers", {
    params: { window, page_size: pageSize, page },
  });
}

export interface NewWriterArticle {
  contentId: string;
  title: string;
  publishedAt?: string;
  /** btl.common.v1.VisibilityLevel, JSON (string) form. */
  visibility: string;
  wordCount: number;
  /** moderation.Tier.String(): "NONE" | "LOW" | "MEDIUM" | "HIGH", or "" if never judged. */
  judgeTier: string;
  /** btl.common.v1.ModerationStatus, JSON (string) form. */
  moderationStatus: string;
  canonicalPath?: string;
}

export interface ListNewWriterArticlesResponse {
  articles: NewWriterArticle[];
}

export function listNewWriterArticles(
  creatorId: string,
  window: NewWriterWindow,
): Promise<ListNewWriterArticlesResponse> {
  return httpRequest<ListNewWriterArticlesResponse>(
    `/v1/admin/new-writers/${encodeURIComponent(creatorId)}/articles`,
    { params: { window } },
  );
}

// ── Authorized publishers (auto-hide allowlist) ────────────────────────────
// Publishers exempt from the spam auto-detector's auto-HIDE (still flagged for
// audit, never auto-hidden) — e.g. @BTL's own paid/sponsored posts.

export interface AuthorizedPublisher {
  publisherId: string;
  note?: string;
  // The two SEO grants. INDEPENDENT of allowlist membership and of each other:
  // being on this list means "never auto-hide", and grants nothing on its own.
  // Absent means not granted — never treat unset as permission.
  dofollowLinksGranted?: boolean;
  searchIndexingGranted?: boolean;
  // Paid-placement disclosure. INVERTED relative to the two grants above: this
  // RESTRICTS rather than permits, so its conservative value is true and the
  // server defaults it ON for a new allowlist row. Absent here means the server
  // did not say — do not render that as "off".
  sponsoredLinks?: boolean;
  addedById?: string;
  createdAt?: string;
  // Populated by admin-service when publisherId resolves to a user via
  // user-service or, failing that, a squad via squad-service. Optional
  // because older admin-service deploys omit them, and publisherId can
  // resolve to neither — callers must fall back to publisherId when these
  // are absent.
  handle?: string;
  displayName?: string;
  avatarUrl?: string;
}

export interface ListAuthorizedPublishersResponse {
  publishers: AuthorizedPublisher[];
}

export function listAuthorizedPublishers(): Promise<ListAuthorizedPublishersResponse> {
  return httpRequest<ListAuthorizedPublishersResponse>(
    "/v1/admin/moderation/authorized-publishers",
  );
}

export interface AddAuthorizedPublisherResponse {
  publisher: AuthorizedPublisher;
}

/**
 * Add a publisher, or edit one — this endpoint is idempotent and doubles as the
 * edit path.
 *
 * The grants are OMITTED from the body when undefined, not sent as false. The
 * server treats an absent field as "leave the existing grant alone" and an
 * explicit `false` as "revoke", so sending false-by-default would mean editing a
 * publisher's note silently revoked its grants.
 */
export function addAuthorizedPublisher(
  publisherId: string,
  note: string,
  grants: { dofollowLinks?: boolean; searchIndexing?: boolean; sponsoredLinks?: boolean } = {},
): Promise<AddAuthorizedPublisherResponse> {
  return httpRequest<AddAuthorizedPublisherResponse>("/v1/admin/moderation/authorized-publishers", {
    method: "POST",
    body: JSON.stringify({
      publisher_id: publisherId,
      note,
      ...(grants.dofollowLinks === undefined
        ? {}
        : { dofollow_links_granted: grants.dofollowLinks }),
      ...(grants.searchIndexing === undefined
        ? {}
        : { search_indexing_granted: grants.searchIndexing }),
      ...(grants.sponsoredLinks === undefined ? {} : { sponsored_links: grants.sponsoredLinks }),
    }),
  });
}

export function removeAuthorizedPublisher(publisherId: string): Promise<unknown> {
  return httpRequest<unknown>(`/v1/admin/moderation/authorized-publishers/${publisherId}`, {
    method: "DELETE",
  });
}

// ── Squad search (for the Authorized Publishers add-picker) ────────────────
// Mirrors listUsers: a lightweight search-by-query used to populate a
// combobox. Callers must tolerate this rejecting (e.g. a 404 against an
// admin-service deploy that predates this endpoint) — see
// authorized-publishers.tsx's combined search, which never lets a squad
// search failure block the user search results.

export interface SquadSearchResult {
  id: string;
  handle?: string;
  displayName?: string;
  avatarUrl?: string;
}

export interface SearchSquadsResponse {
  squads: SquadSearchResult[];
}

export function searchSquads(query: string, limit?: number): Promise<SearchSquadsResponse> {
  return httpRequest<SearchSquadsResponse>("/v1/admin/squads/search", {
    params: {
      q: query,
      limit,
    },
  });
}

export interface FlagContentParams {
  contentId: string;
  reason?: string;
  details?: string;
}

export function flagContent({
  contentId,
  reason,
  details,
}: FlagContentParams): Promise<{ flag: ContentFlagItem }> {
  return httpRequest<{ flag: ContentFlagItem }>(`/v1/admin/content/${contentId}/flag`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? "FLAG_REASON_OTHER", details: details ?? "" }),
  });
}

// ── Moderation probe (LLM-as-judge) ────────────────────────────────────────
// Runs the LLM judge against ad-hoc text and returns its verdict WITHOUT taking
// action. Used to test/tune the rubric before any auto-action is enabled.

export interface ProbeModerationResponse {
  verdict?: string;
  confidence?: number;
  reason?: string;
  recommendedAction?: string;
  tier?: string;
  model?: string;
  rubricVersion?: string;
}

export function probeModeration(
  text: string,
  rubricOverride?: string,
): Promise<ProbeModerationResponse> {
  return httpRequest<ProbeModerationResponse>("/v1/admin/moderation/probe", {
    method: "POST",
    body: JSON.stringify({ text, rubric_override: rubricOverride ?? "" }),
  });
}

// ── LLM judge review (content-service#182, admin-service, wave 3) ───────────
//
// The async LLM-as-judge pass runs on every published article and records one
// row per content id in content_judge_log: tier (NONE/LOW/MEDIUM/HIGH) and
// verdict ("clean" or "spam"), not an append-only history. Reasoning text
// (flagReasoning below) exists ONLY for a "spam" verdict — it is the same
// content_feedback row the judge itself filed (a PENDING flag for LOW/MEDIUM,
// a REVIEWED one for HIGH, which the judge also deranked). A "clean" verdict
// never had reasoning recorded anywhere, so flagId/flagStatus/flagReasoning
// are absent for it — that is the honest state, not a loading gap.
export interface JudgeLogEntry {
  contentId: string;
  contentType: string;
  tier: string;
  verdict: string;
  evaluatedAt?: string;
  /** Hydrated by admin-service; falls back to contentId when absent. */
  contentTitle?: string;
  flagId?: string;
  flagStatus?: string;
  flagReasoning?: string;
}

export interface ListJudgeLogResponse {
  items: JudgeLogEntry[];
  pageInfo?: PageInfo;
}

export interface ListJudgeLogParams {
  /** "clean" or "spam"; omitted means both. */
  verdict?: string;
  contentId?: string;
  pageSize?: number;
  page?: number;
}

export function listJudgeLog({
  verdict,
  contentId,
  pageSize,
  page,
}: ListJudgeLogParams): Promise<ListJudgeLogResponse> {
  return httpRequest<ListJudgeLogResponse>("/v1/admin/moderation/judge-log", {
    params: {
      verdict: verdict || undefined,
      content_id: contentId || undefined,
      page_size: pageSize,
      page,
    },
  });
}

// ── Taxonomy ────────────────────────────────────────────────────────────────

export interface TaxonomyNode {
  id: string;
  kind: string;
  slug: string;
  label: string;
  description?: string;
  status?: string;
  updatedAt?: string;
}

export interface ListTaxonomyNodesResponse {
  nodes: TaxonomyNode[];
  pageInfo?: PageInfo;
}

export interface ListTaxonomyNodesParams {
  query?: string;
  pageSize?: number;
}

// listTaxonomyNodes calls admin-service's BFF proxy, not content-service
// directly: admin-service has no /api/v1/* surface (that path belonged to
// content-service and 404'd here for as long as this page has existed).
// admin-service's ListTaxonomyNodes proxy only forwards page_size as a limit
// and returns pageInfo.totalCount -- content-service ignores page_token and
// never sets a next-page token, so this is a limit-only read today. This page
// has no pager and passes a flat pageSize, so that gap doesn't surface here;
// a future paginated view of this list would need a content-service offset
// fix first.
export function listTaxonomyNodes({
  query,
  pageSize,
}: ListTaxonomyNodesParams): Promise<ListTaxonomyNodesResponse> {
  return httpRequest<ListTaxonomyNodesResponse>("/v1/admin/taxonomy/nodes", {
    params: {
      query: query ?? "",
      page_size: pageSize,
    },
  });
}

// --- Editorial overrides (search ranking) -----------------------------------
//
// PIN is not a stronger boost: it adds an unconditional, unbeatable push to
// position one of search, applied as the last stage of the ranking pipeline so
// nothing downstream can undo it. BOOST/FEATURE/SUPPRESS are ordinary weighted
// nudges that compete with organic ranking; PIN skips that competition
// entirely. Keep that distinction in any UI built on these types — see
// ENTITY_TYPES/OVERRIDE_ACTIONS in editorial-overrides.tsx for how the create
// form encodes it.

export type EntityTypeValue =
  | "ENTITY_TYPE_ARTICLE"
  | "ENTITY_TYPE_THOUGHT"
  | "ENTITY_TYPE_PODCAST"
  | "ENTITY_TYPE_VIDEO"
  | "ENTITY_TYPE_USER"
  | "ENTITY_TYPE_SQUAD"
  | "ENTITY_TYPE_COLLECTION"
  | "ENTITY_TYPE_IMPORTED"
  | "ENTITY_TYPE_NEWSLETTER"
  | "ENTITY_TYPE_VISUAL"
  | "ENTITY_TYPE_GAME"
  | "ENTITY_TYPE_MOMENT"
  | "ENTITY_TYPE_RATING"
  | "ENTITY_TYPE_PREDICTION"
  | "ENTITY_TYPE_GAME_ROUND"
  | "ENTITY_TYPE_PLAYER"
  | "ENTITY_TYPE_TEAM"
  | "ENTITY_TYPE_COMPETITION"
  | "ENTITY_TYPE_COACH";

export type OverrideActionValue =
  | "OVERRIDE_ACTION_BOOST"
  | "OVERRIDE_ACTION_SUPPRESS"
  | "OVERRIDE_ACTION_FEATURE"
  | "OVERRIDE_ACTION_PIN";

export interface EditorialOverride {
  id: string;
  entityId: string;
  entityType: EntityTypeValue;
  action: OverrideActionValue;
  weight: number;
  reason: string;
  createdBy: string;
  startsAt?: string;
  expiresAt?: string;
  outcomeTracked?: boolean;
  outcomeDelta?: number;
  createdAt?: string;
}

export interface ListEditorialOverridesResponse {
  overrides: EditorialOverride[];
  pageInfo?: PageInfo;
}

export interface ListEditorialOverridesParams {
  pageSize?: number;
  pageToken?: string;
  activeOnly?: boolean;
}

export function listEditorialOverrides({
  pageSize,
  pageToken,
  activeOnly,
}: ListEditorialOverridesParams = {}): Promise<ListEditorialOverridesResponse> {
  return httpRequest<ListEditorialOverridesResponse>("/v1/admin/editorial-overrides", {
    params: {
      page_size: pageSize,
      page_token: pageToken,
      active_only: activeOnly ? "true" : undefined,
    },
  });
}

export interface CreateEditorialOverrideParams {
  entityId: string;
  entityType: EntityTypeValue;
  action: OverrideActionValue;
  weight: number;
  reason: string;
  durationSeconds: number;
}

export function createEditorialOverride(
  params: CreateEditorialOverrideParams,
): Promise<EditorialOverride> {
  return httpRequest<EditorialOverride>("/v1/admin/editorial-overrides", {
    method: "POST",
    body: JSON.stringify({
      entityId: params.entityId,
      entityType: params.entityType,
      action: params.action,
      weight: params.weight,
      reason: params.reason,
      // protobuf JSON canonical form for google.protobuf.Duration.
      duration: `${params.durationSeconds}s`,
    }),
  });
}

export function deleteEditorialOverride(overrideId: string): Promise<{ success: boolean }> {
  return httpRequest<{ success: boolean }>(
    `/v1/admin/editorial-overrides/${encodeURIComponent(overrideId)}`,
    { method: "DELETE" },
  );
}

// --- BTL Scholarship (comp memberships) -------------------------------------
//
// A comp is identified by having NO Stripe subscription, never by amountCents
// being zero — a manual grant can carry a non-zero amount and still be a comp
// (see hasStripeSubscription/isComp below, computed server-side). expiresAt is
// the only signal a membership is time-boxed: null/undefined means permanent.

export interface MembershipDTO {
  id: string;
  subscriberId: string;
  // Populated by admin-service PR #32. Optional because older admin-service
  // deploys (and any mocked/fixture data written before that PR) omit them —
  // callers must fall back to subscriberId when these are absent.
  subscriberUsername?: string;
  subscriberDisplayName?: string;
  subscriberAvatarUrl?: string;
  tierId: string;
  tierName?: string;
  status: string;
  amountCents: number;
  currency?: string;
  billingCycle?: string;
  expiresAt?: string | null;
  currentPeriodEnd?: string | null;
  hasStripeSubscription: boolean;
  isComp: boolean;
  grantedBy?: string;
  // Populated by admin-service PR #33: the granting admin's display name
  // (or handle), resolved server-side. Optional because deploys before #33
  // (and any fixtures written before it) only carry the raw grantedBy id —
  // callers must fall back to that id when this is absent.
  grantedByName?: string;
  reason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MembershipTierBillingOptionDTO {
  billingCycle: string;
  priceCents: number;
  isActive: boolean;
}

export interface MembershipTierDTO {
  id: string;
  name: string;
  description?: string;
  /** ISO 4217 lowercase code (e.g. "gbp"). Prod has separate tier rows per
   * currency x cadence sharing a Name (e.g. three "Line Breakers Monthly"
   * rows, one per GBP/USD/EUR) — this is what tells them apart. */
  currency?: string;
  /** Stringified btl.common.v1.UserTier ("USER_TIER_FREE" | "USER_TIER_PRO" |
   * "USER_TIER_LINE_BREAKERS") — the platform access tier this row grants.
   * Group/select on this, never on name, to collapse the currency x cadence
   * rows down to one option per platform tier (see lib/scholarship.ts's
   * selectCanonicalTierOptions). */
  platformTierMapping?: string;
  isActive: boolean;
  billingOptions: MembershipTierBillingOptionDTO[];
}

export function listPlatformMembershipTiers(): Promise<{ tiers: MembershipTierDTO[] }> {
  return httpRequest<{ tiers: MembershipTierDTO[] }>("/v1/admin/memberships/tiers");
}

export interface GrantMembershipParams {
  subscriberId: string;
  tierId: string;
  reason: string;
  /** Absolute instant (ISO 8601), resolved client-side from the duration
   * control. Omit/undefined for a permanent grant — no duration is ever sent,
   * only the resolved instant, per GrantMembershipRequest.expires_at. */
  expiresAt?: string;
}

export function grantMembership(
  params: GrantMembershipParams,
): Promise<{ membership: MembershipDTO }> {
  return httpRequest<{ membership: MembershipDTO }>("/v1/admin/memberships/grant", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export function revokeMembership(membershipId: string): Promise<{ membership: MembershipDTO }> {
  return httpRequest<{ membership: MembershipDTO }>(
    `/v1/admin/memberships/${encodeURIComponent(membershipId)}/revoke`,
    { method: "POST" },
  );
}

export function getUserPlatformMembership(
  userId: string,
): Promise<{ found: boolean; membership: MembershipDTO | null }> {
  return httpRequest<{ found: boolean; membership: MembershipDTO | null }>(
    `/v1/admin/users/${encodeURIComponent(userId)}/membership`,
  );
}

// listComplimentaryMemberships and setMembershipExpiry are wired to real
// routes, but those routes are not implemented server-side yet — both
// currently reject with HTTP 501 and a message explaining exactly what
// subscription-service change closes the gap. See admin-service's
// memberships.go (handleListComps / handleSetMembershipExpiry) for why: no
// existing RPC enumerates memberships across subscribers for an admin
// caller, and none accepts an admin-chosen arbitrary expiry on an existing
// membership. Kept as real calls (not stubbed client-side) so the manage
// view's error state reports the actual backend reason, not a fabricated one.

export interface ListComplimentaryMembershipsResponse {
  memberships: MembershipDTO[];
}

export function listComplimentaryMemberships(): Promise<ListComplimentaryMembershipsResponse> {
  return httpRequest<ListComplimentaryMembershipsResponse>("/v1/admin/memberships/comps");
}

export function setMembershipExpiry(
  membershipId: string,
  expiresAt: string,
): Promise<{ membership: MembershipDTO }> {
  return httpRequest<{ membership: MembershipDTO }>(
    `/v1/admin/memberships/${encodeURIComponent(membershipId)}/expiry`,
    {
      method: "POST",
      body: JSON.stringify({ expiresAt }),
    },
  );
}

// makeMembershipPermanent clears a comp's end date so it never expires. Hits
// the same endpoint as setMembershipExpiry but with the makePermanent branch of
// the server's expiry oneof, so the two can never be sent together.
export function makeMembershipPermanent(
  membershipId: string,
): Promise<{ membership: MembershipDTO }> {
  return httpRequest<{ membership: MembershipDTO }>(
    `/v1/admin/memberships/${encodeURIComponent(membershipId)}/expiry`,
    {
      method: "POST",
      body: JSON.stringify({ makePermanent: true }),
    },
  );
}

// ---------------------------------------------------------------------------
// Advertiser / partner provisioning (admin-service#37)
// ---------------------------------------------------------------------------
//
// An advertiser is a NORMAL FREE-TIER USER plus a scoped, expiring capability
// grant (CAPABILITY_PUBLISH_GUEST_CONTENT). There is no advertiser tier and no
// advertiser role — the permission IS the grant, and when it lapses the account
// goes back to being an ordinary one.
//
// The identifiers here say advertiser/partner and never "guest": ADR-049 owns
// that word for broadcast guests. The capability enum's own name is protos' and
// is left alone.

/** A grant's derived state, computed server-side. See admin-service's grantStatus. */
export type AdvertiserGrantStatus = "active" | "scheduled" | "expired" | "revoked" | "exhausted";

export interface AdvertiserGrant {
  id: string;
  principalId: string;
  principalType: string;
  capability: string;
  /** Lifetime allowance for this grant. 0 means unmetered — see isUnmetered. */
  quotaLimit: number;
  quotaUsed: number;
  isUnmetered: boolean;
  /** Absent on an unmetered grant, where "remaining" is meaningless. */
  quotaRemaining?: number;
  startsAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  /** auth-service's own record of the admin who issued this. */
  grantedBy?: string;
  grantedByName?: string;
  reason?: string;
  /** Resolved from user-service for display; absent when resolution failed. */
  principalHandle?: string;
  principalName?: string;
  status: AdvertiserGrantStatus;
}

export interface ListAdvertiserGrantsParams {
  /** Runtime-resolved principal id from a live search — never a constant. */
  principalId?: string;
  includeRevoked?: boolean;
  includeExpired?: boolean;
  pageSize?: number;
  pageToken?: string;
}

export interface ListAdvertiserGrantsResponse {
  grants: AdvertiserGrant[];
  pageInfo?: PageInfo;
  /**
   * Whether admin-service's best-effort user-service lookup actually ran and
   * succeeded for this page of grants.
   *
   * A USER principal resolving to no profile is badged "unknown" by
   * deriveAdvertiserStatus (see advertisers.ts) — NOT "not verified yet":
   * admin-service's Register now creates the profile at account creation
   * (admin-service#56), so profile presence says nothing about verification
   * either way. This flag exists so a failed lookup reads as "unknown"
   * rather than as a claim about the accounts: when it did not run, every
   * row comes back equally bare, and without this flag the whole table would
   * badge itself "unknown" regardless of the real account state. Absent (an
   * admin-service build that predates the flag) is treated as "did not
   * resolve", which degrades the same way.
   */
  identitiesResolved?: boolean;
}

export function listAdvertiserGrants(
  params: ListAdvertiserGrantsParams = {},
): Promise<ListAdvertiserGrantsResponse> {
  return httpRequest<ListAdvertiserGrantsResponse>("/v1/admin/advertisers/grants", {
    params: {
      principal_id: params.principalId || undefined,
      include_revoked: params.includeRevoked ? true : undefined,
      include_expired: params.includeExpired ? true : undefined,
      page_size: params.pageSize,
      page_token: params.pageToken || undefined,
    },
  });
}

export interface GrantAdvertiserCapabilityParams {
  principalId: string;
  /** Defaults to USER server-side; sent only when a squad is being granted. */
  principalType?: "PRINCIPAL_TYPE_USER" | "PRINCIPAL_TYPE_SQUAD";
  /** 0 = unmetered. */
  quotaLimit: number;
  /** Absolute RFC3339 instant. Required — a grant must be time-boxed. */
  expiresAt: string;
  startsAt?: string;
  reason: string;
}

export function grantAdvertiserCapability(
  params: GrantAdvertiserCapabilityParams,
): Promise<{ grant: AdvertiserGrant }> {
  return httpRequest<{ grant: AdvertiserGrant }>("/v1/admin/advertisers/grants", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export function revokeAdvertiserGrant(
  grantId: string,
  reason: string,
): Promise<{ grant: AdvertiserGrant }> {
  return httpRequest<{ grant: AdvertiserGrant }>(
    `/v1/admin/advertisers/grants/${encodeURIComponent(grantId)}/revoke`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

/**
 * AdjustAdvertiserCapabilityGrantParams is a partial update: omitting a field
 * means "leave it alone" (admin-service's handleAdjustAdvertiserCapabilityGrant
 * treats an absent JSON key the same way, via a nil pointer). quotaLimit=0
 * means unmetered, same convention as GrantAdvertiserCapabilityParams.
 * quota_used is never part of this call — it is not something this RPC
 * touches (see AdjustPrincipalCapabilityGrant's proto doc, protos v0.145.0).
 */
export interface AdjustAdvertiserCapabilityGrantParams {
  quotaLimit?: number;
  /** Absolute RFC3339 instant. May move the window either direction. */
  expiresAt?: string;
  reason: string;
}

/**
 * Raises (or sets) a grant's lifetime quota and/or moves its expiry, without
 * touching quota_used — the "give back credit" / "extend the window" remedy
 * for a grant that already exists. Rejected (409) for a revoked grant.
 */
export function adjustAdvertiserCapabilityGrant(
  grantId: string,
  params: AdjustAdvertiserCapabilityGrantParams,
): Promise<{ grant: AdvertiserGrant }> {
  return httpRequest<{ grant: AdvertiserGrant }>(
    `/v1/admin/advertisers/grants/${encodeURIComponent(grantId)}/adjust`,
    {
      method: "POST",
      body: JSON.stringify(params),
    },
  );
}

/**
 * Admin-only remediation for the T2c publish-once gate: a piece of content
 * published under an advertiser capability grant locks to its published
 * revision, so a fix needs an explicit unlock. Admits exactly ONE more
 * revision cycle; content-service clears the flag itself on use, so a second
 * fix needs a second unlock. Rejected (409) for content that was never
 * published under an advertiser grant — there is nothing to unlock.
 *
 * The advertiser panel has no content listing to key this off of, so this
 * takes a raw content id — see the panel's "Unlock a revision" section.
 */
export function unlockAdvertiserContentRevision(
  contentId: string,
  reason: string,
): Promise<{ contentId: string; unlockedAt?: string }> {
  return httpRequest<{ contentId: string; unlockedAt?: string }>(
    `/v1/admin/advertisers/content/${encodeURIComponent(contentId)}/unlock-revision`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export interface CreateAdvertiserParams {
  email: string;
  username: string;
  displayName?: string;
}

/**
 * The response to creating an advertiser account — and the ONLY time the
 * temporary password is ever available.
 *
 * It is not persisted by admin-service, not logged anywhere, and cannot be
 * fetched again. Nothing in this app may write it to localStorage, to a store,
 * to a query cache, or to the URL. It travels from this call straight into the
 * handover dialog's component state and dies with that component.
 */
export interface CreateAdvertiserResponse {
  /**
   * The principal id auth-service's Register RPC returns immediately
   * (auth-service#79) — real, not synthesised. Usable against user-service
   * right away: the profile exists by the time this responds, under the
   * handle and display name sent in the request (auth-service#81).
   */
  userId?: string;
  email: string;
  username?: string;
  displayName?: string;
  tier: string;
  temporaryPassword: string;
  /**
   * Always true. auth-service creates accounts unverified and its Login refuses
   * an unverified identity, so the advertiser must click the verification email
   * before the password is usable at all.
   */
  pendingEmailVerification: boolean;
  /**
   * Whether the platform can COMPEL a password change at first login. False
   * today — auth-service has no such mechanism (see the handover dialog). It is
   * a field rather than a hardcoded string so the UI stops telling operators to
   * ask nicely the day that changes.
   */
  passwordChangeEnforced: boolean;
}

export function createAdvertiser(
  params: CreateAdvertiserParams,
): Promise<CreateAdvertiserResponse> {
  return httpRequest<CreateAdvertiserResponse>("/v1/admin/advertisers", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

/**
 * Revokes every session for an existing account and returns a one-time Kratos
 * recovery link (valid 24h) for the operator to hand over out of band.
 *
 * This is the invite-link-then-set-password handover ADR-049 settled on: the
 * admin never learns the account's password, the recipient sets their own. The
 * returned link is itself a credential and gets the same treatment as the
 * temporary password — shown once, never stored.
 */
export function issueAdvertiserRecoveryLink(
  userId: string,
  reason: string,
): Promise<{ recoveryLink?: string; sessionsRevoked: number; passwordChangeEnforced: boolean }> {
  return httpRequest<{
    recoveryLink?: string;
    sessionsRevoked: number;
    passwordChangeEnforced: boolean;
  }>(`/v1/admin/advertisers/${encodeURIComponent(userId)}/recovery-link`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// ── Audit trail ─────────────────────────────────────────────────────────────
//
// admin-service records one row per admin mutation it serves: who acted, what
// they did, to which entity, why, and whether it succeeded. Reads are not
// recorded. See admin-service internal/api/httpapi/audit.go.
//
// The endpoint answers 503 when admin-service has no audit database configured.
// httpRequest surfaces that as an Error carrying the service's message, which
// the page shows rather than rendering an empty table: an empty table reads as
// "no admin has done anything", which is a much worse answer than "this is not
// wired up".

export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorId: string;
  /** Resolved for display. Absent when the account no longer exists, or when
   *  the name lookup failed; the page falls back to actorId. */
  actorUsername?: string;
  actorDisplayName?: string;
  action: string;
  entityType?: string;
  entityId?: string;
  reason?: string;
  outcome: string;
  statusCode: number;
  requestMethod?: string;
  requestPath?: string;
  traceId?: string;
}

export interface ListAuditEventsResponse {
  events: AuditEvent[];
  pageInfo?: PageInfo;
}

export interface ListAuditEventsParams {
  /** Free-text over actor, action, entity id and reason, applied server-side
   *  before paging. */
  query?: string;
  actorId?: string;
  action?: string;
  entityType?: string;
  /**
   * Bare YYYY-MM-DD (what an `<input type="date">` sends). Inclusive: the
   * server treats `from` as the start of that UTC day and `to` as covering
   * the whole named day, not just its midnight instant. RFC3339 is also
   * accepted by the server but the page only ever sends date-only.
   */
  from?: string;
  to?: string;
  pageSize?: number;
  /** 1-based, matching listAdmins and listContentFlags. */
  page?: number;
}

export function listAuditEvents({
  query,
  actorId,
  action,
  entityType,
  from,
  to,
  pageSize,
  page,
}: ListAuditEventsParams = {}): Promise<ListAuditEventsResponse> {
  return httpRequest<ListAuditEventsResponse>("/v1/admin/audit-events", {
    params: {
      query: query || undefined,
      actor_id: actorId || undefined,
      action: action || undefined,
      entity_type: entityType || undefined,
      from: from || undefined,
      to: to || undefined,
      page_size: pageSize,
      page,
    },
  });
}

// ── Platform-static pages ───────────────────────────────────────────────────
//
// BTL's own legal and corporate pages (ADR-025). admin-service is the
// authorization gateway and owns the audit trail; content-service stores each
// page as a PageComposition on a `PAGE_SURFACE_PLATFORM_*` surface. The BFF
// flattens that composition to the document shape below, so nothing here deals
// in blocks.

export interface PlatformPageSummary {
  slug: string;
  title: string;
  /** The public path on Platform this page is served at. */
  route: string;
  surface: string;
  /** Lexical document. Empty on a page nobody has authored. */
  bodyJson: string;
  /** Optimistic-concurrency token; echoed back on save. */
  version: string;
  /** False while the live route is still serving hardcoded copy. */
  isAuthored: boolean;
  updatedAt?: string | null;
}

export function listPlatformPages(): Promise<{ pages: PlatformPageSummary[] }> {
  return httpRequest<{ pages: PlatformPageSummary[] }>("/v1/admin/pages");
}

export function getPlatformPage(pageSlug: string): Promise<PlatformPageSummary> {
  return httpRequest<PlatformPageSummary>(`/v1/admin/pages/${pageSlug}`);
}

export interface UpdatePlatformPageParams {
  pageSlug: string;
  title: string;
  bodyJson: string;
  /** The version last read. A stale one comes back as 409. */
  expectedVersion: string;
}

export function updatePlatformPage({
  pageSlug,
  title,
  bodyJson,
  expectedVersion,
}: UpdatePlatformPageParams): Promise<PlatformPageSummary> {
  return httpRequest<PlatformPageSummary>(`/v1/admin/pages/${pageSlug}`, {
    method: "PUT",
    body: JSON.stringify({ title, bodyJson, expectedVersion }),
  });
}

/**
 * One earlier version of a page.
 *
 * content-service snapshots on write, so a version is the text as it stood
 * BEFORE a change replaced it. The text a page carries right now is never in
 * this list.
 *
 * No body: the list says which version, and restoring returns the text.
 */
export interface PlatformPageRevision {
  revisionId: string;
  /** Title as it stood in this version. */
  title: string;
  /** When this version was saved, and who saved it. */
  savedAt?: string | null;
  savedById?: string;
  savedByUsername?: string;
  savedByName?: string;
  /** When it stopped being live, and what replaced it: edit, reset or restore. */
  replacedAt?: string | null;
  replacedBy: string;
  /** Set only on a version a restore replaced. */
  reason?: string;
}

export function listPlatformPageRevisions(
  pageSlug: string,
): Promise<{ revisions: PlatformPageRevision[]; nextPageToken?: string }> {
  return httpRequest<{ revisions: PlatformPageRevision[]; nextPageToken?: string }>(
    `/v1/admin/pages/${pageSlug}/revisions`,
  );
}

export interface RestorePlatformPageRevisionParams {
  pageSlug: string;
  revisionId: string;
  reason: string;
  /** The version last read. A stale one comes back as 409. */
  expectedVersion: string;
}

/** Returns the page as it now stands, restored. */
export function restorePlatformPageRevision({
  pageSlug,
  revisionId,
  reason,
  expectedVersion,
}: RestorePlatformPageRevisionParams): Promise<PlatformPageSummary> {
  return httpRequest<PlatformPageSummary>(
    `/v1/admin/pages/${pageSlug}/revisions/${revisionId}/restore`,
    {
      method: "POST",
      body: JSON.stringify({ reason, expectedVersion }),
    },
  );
}

// =============================================================================
// REVENUE
// =============================================================================

/**
 * The ledger types below are subscription-ledger truth: what
 * subscription-service's memberships table says is currently subscribed. None
 * of it is reconciled against Stripe, so refunds, Stripe fees and mid-cycle
 * failed renewals are not reflected.
 *
 * The settlement types further down (PlatformPayoutSummary) are the other
 * side: what Stripe has actually paid and holds. The two will not agree and
 * nothing reconciles them.
 */

/**
 * PLATFORM is a subscription to BTL, where BTL keeps the whole amount.
 * PRINCIPAL is a subscription to a creator or squad, where BTL keeps only its
 * platform fee. Never add the two together.
 */
export type RevenueBillingContext = "PLATFORM" | "PRINCIPAL" | "UNKNOWN";

export type RevenueBillingCycle = "MONTHLY" | "ANNUAL" | "UNKNOWN";

/** MRR for one billing context in one currency. Currencies are never summed. */
export interface RevenueMRR {
  billingContext: RevenueBillingContext;
  /** Lower-case ISO-4217. */
  currency: string;
  activeSubscriptions: number;
  /** Contracted but not yet charged. Included in monthlyCents. */
  trialingSubscriptions: number;
  monthlyCents: number;
}

export interface RevenueTier {
  billingContext: RevenueBillingContext;
  tierId: string;
  tierName?: string;
  currency: string;
  billingCycle: RevenueBillingCycle;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  monthlyCents: number;
}

export interface RevenuePrincipal {
  principalId: string;
  principalType: string;
  handle?: string;
  displayName?: string;
  avatarUrl?: string;
  currency: string;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  /** Gross subscriber spend, not BTL's cut and not the principal's net. */
  monthlyCents: number;
}

export interface RevenueLifecycle {
  billingContext: RevenueBillingContext;
  newCount: number;
  churnedCount: number;
}

export type RevenuePeriod = "this_month" | "last_30d";

export interface RevenueSummary {
  /** The period the service actually answered, which may differ from the one asked for. */
  period: RevenuePeriod;
  windowStart: string;
  windowEnd: string;
  calculatedAt?: string;
  mrr: RevenueMRR[];
  tiers: RevenueTier[];
  principals: RevenuePrincipal[];
  lifecycle: RevenueLifecycle[];
}

export interface GetRevenueSummaryParams {
  period?: RevenuePeriod;
  principalLimit?: number;
}

export function getRevenueSummary({
  period,
  principalLimit,
}: GetRevenueSummaryParams = {}): Promise<RevenueSummary> {
  return httpRequest<RevenueSummary>("/v1/admin/revenue/summary", {
    params: {
      period,
      principal_limit: principalLimit,
    },
  });
}

// =============================================================================
// PAYOUTS (Stripe settlement)
// =============================================================================

/**
 * Everything below is STRIPE SETTLEMENT truth, not the subscription ledger
 * above: what Stripe has actually paid out and what it currently holds.
 *
 * The two will not agree, and nothing here or in any service reconciles them.
 * Refunds, disputes, Stripe fees, the hold period and Stripe's payout schedule
 * all sit between what subscriptions are contracted to bill and what money has
 * moved. A difference between the MRR figures and these is not a discrepancy.
 *
 * SCOPE is BTL's own Stripe account. Creators and squads are merchant of
 * record on their own connected accounts, and Stripe pays them out directly -
 * none of that appears here. What does: BTL's platform-tier subscription
 * revenue plus the platform fees collected on creator subscriptions.
 */

/**
 * PENDING accepted but not yet sent, IN_TRANSIT sent but not yet landed,
 * PAID landed in the bank, FAILED rejected by the bank, CANCELED withdrawn
 * before sending. UNKNOWN means Stripe reported a state the API does not map -
 * rendered as itself, never guessed, because PAID and FAILED mean opposite
 * things about whether BTL has the money.
 */
export type PlatformPayoutStatus =
  | "PENDING"
  | "IN_TRANSIT"
  | "PAID"
  | "FAILED"
  | "CANCELED"
  | "UNKNOWN";

/**
 * The Stripe balance in ONE currency.
 *
 * There is deliberately no combined total. `availableMinorUnits` and
 * `pendingMinorUnits` are separate claims about the same account - what can be
 * paid out now, and what cannot yet - and their sum is a balance the account
 * does not have. Currencies are never merged either: there is no exchange rate
 * anywhere in this system.
 */
export interface PlatformBalance {
  /** Lower-case ISO-4217. Part of the row's identity, not a label. */
  currency: string;
  /** Settled, free for the next payout, in MINOR units. 123456 gbp is GBP 1,234.56. */
  availableMinorUnits: number;
  /** Taken but not yet settled, in MINOR units. Never added to available. */
  pendingMinorUnits: number;
}

/** One payout from BTL's Stripe account to BTL's bank. */
export interface PlatformPayout {
  /** Stripe's payout id ("po_..."). There is no BTL record behind it. */
  id: string;
  /** MINOR units, in this payout's own currency. */
  amountMinorUnits: number;
  currency: string;
  status: PlatformPayoutStatus;
  /** When the funds are expected in, or landed in, the bank. Absent if Stripe did not report one. */
  arrivalDate?: string;
  createdAt?: string;
}

export interface PlatformPayoutSummary {
  /** One row per currency. Never merged. */
  balances: PlatformBalance[];
  /** Newest first, capped by the server. */
  recentPayouts: PlatformPayout[];
  /** When Stripe was read. A live read with no cache. */
  retrievedAt?: string;
}

export interface GetPlatformPayoutSummaryParams {
  payoutLimit?: number;
}

export function getPlatformPayoutSummary({
  payoutLimit,
}: GetPlatformPayoutSummaryParams = {}): Promise<PlatformPayoutSummary> {
  return httpRequest<PlatformPayoutSummary>("/v1/admin/revenue/payouts", {
    params: {
      payout_limit: payoutLimit,
    },
  });
}

// =============================================================================
// PROMO CODES
// =============================================================================

/**
 * A promo code is a Stripe promotion code plus the coupon behind it. This
 * client holds the two together because that is the shape admin-service
 * returns, and because the coupon is where the discount lives: the code is
 * only the string a subscriber types.
 */

/**
 * The discount is a discriminated union with exactly one shape per `kind`.
 *
 * UNKNOWN is not a third kind of discount. It is admin-service saying it could
 * not read the coupon behind the code, and it carries no other field - so
 * nothing may render a number for it. See discountLabel in lib/promo-codes.ts.
 */
export type PromoCodeDiscount =
  | { kind: "PERCENT"; percentOff: number }
  /** Minor units, e.g. 500 with currency "gbp" is £5.00. */
  | { kind: "AMOUNT"; amountOffMinorUnits: number; currency: string }
  | { kind: "UNKNOWN" };

/** The discount a create call may ask for. UNKNOWN is a read-side state only. */
export type PromoCodeDiscountInput = Exclude<PromoCodeDiscount, { kind: "UNKNOWN" }>;

/**
 * ONCE discounts the first payment, REPEATING the next `durationInMonths`
 * months, FOREVER every payment. UNKNOWN, like the discount's, means the
 * coupon could not be read.
 */
export type PromoCodeDuration = "ONCE" | "REPEATING" | "FOREVER" | "UNKNOWN";

export type PromoCodeDurationInput = Exclude<PromoCodeDuration, "UNKNOWN">;

export interface PromoCode {
  id: string;
  code: string;
  couponId: string;
  discount: PromoCodeDiscount;
  duration: PromoCodeDuration;
  /** Non-zero only when duration is REPEATING. */
  durationInMonths: number;
  /** 0 means unlimited. */
  maxRedemptions: number;
  timesRedeemed: number;
  /**
   * Deactivating is not deleting. An inactive code cannot be redeemed and
   * still appears in the list, with its redemption history intact.
   */
  active: boolean;
  /** Omitted when the code never expires. */
  expiresAt?: string;
  createdAt?: string;
}

export interface ListPromoCodesParams {
  pageSize?: number;
  pageToken?: string;
  activeOnly?: boolean;
}

export interface ListPromoCodesResponse {
  promoCodes?: PromoCode[];
  nextPageToken?: string;
}

export function listPromoCodes({
  pageSize,
  pageToken,
  activeOnly,
}: ListPromoCodesParams = {}): Promise<ListPromoCodesResponse> {
  return httpRequest<ListPromoCodesResponse>("/v1/admin/promo-codes", {
    params: {
      page_size: pageSize,
      page_token: pageToken || undefined,
      active_only: activeOnly,
    },
  });
}

export interface CreatePromoCodeParams {
  code: string;
  discount: PromoCodeDiscountInput;
  duration: PromoCodeDurationInput;
  /**
   * Required and greater than zero for REPEATING, and exactly 0 for ONCE and
   * FOREVER. It is derived from `duration` rather than typed independently -
   * see buildCreatePromoCodeRequest in lib/promo-codes.ts.
   */
  durationInMonths: number;
  /** 0 means unlimited. */
  maxRedemptions: number;
  /** RFC3339. Omit entirely for a code that never expires. */
  expiresAt?: string;
}

export function createPromoCode(params: CreatePromoCodeParams): Promise<{ promoCode: PromoCode }> {
  return httpRequest<{ promoCode: PromoCode }>("/v1/admin/promo-codes", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

/**
 * Stops the code being redeemable. The reason is the audit trail for why a
 * live discount was pulled, so it is mandatory here as it is on the advertiser
 * grant revoke.
 */
export function deactivatePromoCode(
  promoCodeId: string,
  reason: string,
): Promise<{ promoCode: PromoCode }> {
  return httpRequest<{ promoCode: PromoCode }>(
    `/v1/admin/promo-codes/${encodeURIComponent(promoCodeId)}/deactivate`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

// =============================================================================
// ANALYTICS
// =============================================================================

// Platform analytics, from BTL's own data: reader events recorded by
// intelligence-service and publishing rows owned by content-service.
//
// Not Google Analytics. Nothing here is reconciled against a web analytics
// property, and there is no GA connection to reconcile against.
//
// The two series count different populations and no figure on one divides into
// a figure on another. Traffic counts reader events; publishing counts rows
// that are published now, bucketed by when they landed.

/** The three windows both series accept. There is no "all". */
export type AnalyticsRange = "7d" | "30d" | "90d";

/** Bucket width. Week buckets start Monday, UTC. */
export type AnalyticsBucket = "day" | "week";

export interface TrafficPoint {
  /** RFC3339, UTC, truncated to the bucket width. */
  bucketStart: string;
  views: number;
  /**
   * Distinct readers in THIS bucket. Not additive: a reader active on three
   * days counts once in each of the three daily buckets, so summing this
   * across the series does not give unique readers over the window. Plot it,
   * never total it.
   */
  uniqueReaders: number;
}

/** One scheduled game, for the kickoff markers on the traffic chart. */
export interface AnalyticsFixture {
  gameId: string;
  /** A competition id, not a name. game-service carries no label for it. */
  competitionId?: string;
  /** Empty when the team is not crosswalked to a BTL identity yet. */
  homeTeam?: string;
  awayTeam?: string;
  scheduledStart: string;
}

export interface AnalyticsTraffic {
  range: AnalyticsRange;
  bucket: AnalyticsBucket;
  points: TrafficPoint[];
  fixtures: AnalyticsFixture[];
  /**
   * The fixture overlay failed and the traffic figures were served anyway.
   *
   * Read this rather than inferring from an empty `fixtures`. A window with no
   * fixtures in it is ordinary (an international break) and must not be
   * reported as a broken feed.
   */
  fixturesUnavailable: boolean;
}

export interface GetAnalyticsTrafficParams {
  range?: AnalyticsRange;
  bucket?: AnalyticsBucket;
}

export function getAnalyticsTraffic({
  range,
  bucket,
}: GetAnalyticsTrafficParams = {}): Promise<AnalyticsTraffic> {
  return httpRequest<AnalyticsTraffic>("/v1/admin/analytics/traffic", {
    params: { range, bucket },
  });
}

export interface TopContentItem {
  entityId: string;
  entityType: string;
  /**
   * Hydrated by admin-service from content-service. Empty when the content is
   * deleted or unreadable, which is a real state rather than a loading gap:
   * something read heavily last week and deleted yesterday still belongs on a
   * read leaderboard. Fall back to entityId.
   */
  title: string;
  views: number;
  /** Distinct readers for this entity over the whole window. */
  uniqueReaders: number;
}

export interface AnalyticsTopContent {
  range: AnalyticsRange;
  /** The limit actually applied, which may be lower than the one asked for. */
  limit: number;
  items: TopContentItem[];
  /**
   * Title hydration could not run at all. Individual misses are not flagged
   * here; they show as an empty `title`.
   */
  titlesUnavailable: boolean;
}

export interface GetAnalyticsTopContentParams {
  range?: AnalyticsRange;
  limit?: number;
}

export function getAnalyticsTopContent({
  range,
  limit,
}: GetAnalyticsTopContentParams = {}): Promise<AnalyticsTopContent> {
  return httpRequest<AnalyticsTopContent>("/v1/admin/analytics/top-content", {
    params: { range, limit },
  });
}

export interface PublishingPoint {
  bucketStart: string;
  publishedCount: number;
  /** Not additive, same rule as TrafficPoint.uniqueReaders. */
  distinctCreators: number;
}

export interface AnalyticsPublishing {
  range: AnalyticsRange;
  bucket: AnalyticsBucket;
  points: PublishingPoint[];
}

export interface GetAnalyticsPublishingParams {
  range?: AnalyticsRange;
  bucket?: AnalyticsBucket;
}

export function getAnalyticsPublishing({
  range,
  bucket,
}: GetAnalyticsPublishingParams = {}): Promise<AnalyticsPublishing> {
  return httpRequest<AnalyticsPublishing>("/v1/admin/analytics/publishing", {
    params: { range, bucket },
  });
}

// --- Google Analytics --------------------------------------------------------
//
// A DIFFERENT POPULATION from everything above. The types above count BTL's own
// reader events and publishing rows; these count what a web analytics property
// saw. A session is a browser, not a reader, and a page view is a URL load, not
// an article read. Ad blockers, consent declines, bots and prerender move the
// two apart in both directions and nothing reconciles them. No figure here
// divides into a figure above.
//
// This ships DARK. admin-service is deployed with no property and no key, and
// the endpoint then answers 200 with `configured: false`. That is the normal
// state, not a failure, and the section renders one line for it.

export interface GAPage {
  /**
   * The path the analytics property recorded. Not resolved to a BTL content id
   * and not joinable against the read leaderboard: the property saw a URL, not
   * a piece of content.
   */
  path: string;
  views: number;
}

export interface GASource {
  /**
   * The property's own source label, unfiltered. "(direct)" is a real value and
   * appears as a row rather than being tidied away. It is usually the largest
   * one, and a table that dropped it would misstate where traffic comes from.
   */
  source: string;
  sessions: number;
}

export interface GACountry {
  /**
   * GA's own display name, not an ISO code, so it is for reading and not for
   * joining. "(not set)" is a real row and is kept: dropping it would leave
   * the remaining shares describing a population that does not exist.
   */
  country: string;
  users: number;
}

export interface GASegment {
  /** GA's bucket label, verbatim: "25-34", "female", "unknown". */
  segment: string;
  users: number;
}

export interface GoogleAnalyticsSummary {
  /**
   * False when no property and key are wired, and everything else is then zero
   * or empty.
   *
   * Branch on THIS, never on `sessions === 0`. A configured property with no
   * traffic yet is a real and different state, and reporting it as "not
   * connected" would send someone to check credentials that are fine.
   */
  configured: boolean;
  range: AnalyticsRange;
  /**
   * Window totals. `users` is distinct users over the WHOLE window, not a sum
   * of daily figures, so it is smaller than adding up seven days. None of the
   * three is derivable from another.
   */
  sessions: number;
  users: number;
  pageViews: number;
  topPages: GAPage[];
  topSources: GASource[];
  /** Where the audience is, by users, descending. */
  topCountries: GACountry[];
  /**
   * GA's demographic buckets, by users, descending.
   *
   * EMPTY MEANS "THE PROPERTY DID NOT REPORT IT", not "nobody". GA only fills
   * these when Google Signals is on and a bucket clears its thresholding
   * minimum, so render absence as unavailable rather than as an audience with
   * no ages. They also do not sum to `users`: withheld buckets are missing by
   * construction, so any share must divide by the segments' own total.
   */
  ageBrackets: GASegment[];
  genders: GASegment[];
  /**
   * When the property was actually READ, RFC3339. Not when this response was
   * written.
   *
   * admin-service caches these figures for fifteen minutes because GA quota is
   * metered per report, so this is routinely minutes old, and during a Google
   * outage it can be older (the cache serves the last good read rather than
   * emptying the section). The page renders the age from this field. Absent
   * when nothing has been read, which is every response while unconfigured.
   */
  cachedAt?: string;
}

export interface GetGoogleAnalyticsSummaryParams {
  range?: AnalyticsRange;
}

export function getGoogleAnalyticsSummary({
  range,
}: GetGoogleAnalyticsSummaryParams = {}): Promise<GoogleAnalyticsSummary> {
  return httpRequest<GoogleAnalyticsSummary>("/v1/admin/analytics/ga/summary", {
    params: { range },
  });
}

// --- Search Console ----------------------------------------------------------
//
// A THIRD population, distinct from both the reader events above and the
// Google Analytics section. A click here is a click in a Google search
// result, not a page view and not an article read. Nothing here reconciles
// against either of the other two.
//
// Ships DARK on the same contract as Google Analytics: not configured answers
// 200 with configured: false, never an error.

export interface SearchConsoleTrendPoint {
  /** "YYYY-MM-DD" in Search Console's own reporting time zone (PT). */
  date: string;
  clicks: number;
}

export interface SearchConsoleQuery {
  query: string;
  clicks: number;
  impressions: number;
  /** A fraction (0..1), not a percentage. */
  ctr: number;
  /** Average ranking position, 1-based. Lower is better. */
  position: number;
}

export interface SearchConsolePage {
  /** The full URL as Search Console recorded it. */
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface SearchConsoleUnderperformingPage extends SearchConsolePage {
  /**
   * The benchmark CTR this page's own CTR is being measured against, from a
   * fixed organic-CTR-by-position curve. A reference line, not a figure
   * Search Console reports.
   */
  expectedCtr: number;
}

export interface SearchConsoleSitemaps {
  sitemapCount: number;
  submitted: number;
  pending: number;
  errors: number;
  warnings: number;
  /** True when the sitemap read failed. Independent of the search figures. */
  unavailable: boolean;
}

export interface SearchConsoleSummary {
  configured: boolean;
  range: AnalyticsRange;
  clicks: number;
  impressions: number;
  ctr: number;
  averagePosition: number;
  /** Clicks per day. Non-nil. */
  trend: SearchConsoleTrendPoint[];
  topQueries: SearchConsoleQuery[];
  topPages: SearchConsolePage[];
  /** Pages ranking well with a CTR far below what that position usually earns. */
  underperforming: SearchConsoleUnderperformingPage[];
  sitemaps: SearchConsoleSitemaps;
  /** When Search Console was actually read, RFC3339. Absent when unconfigured. */
  cachedAt?: string;
}

export interface GetSearchConsoleSummaryParams {
  range?: AnalyticsRange;
}

export function getSearchConsoleSummary({
  range,
}: GetSearchConsoleSummaryParams = {}): Promise<SearchConsoleSummary> {
  return httpRequest<SearchConsoleSummary>("/v1/admin/analytics/search/summary", {
    params: { range },
  });
}

// --- Home deltas -------------------------------------------------------------
//
// The week-on-week movement behind the Home page's numbers, read out of
// admin-service's daily snapshot table. Every value is nullable and null means
// NOT RECORDED, never zero: history only accumulates from the day the snapshot
// job first ran and cannot be backfilled, so a metric with no baseline shows no
// delta rather than an invented "no change".

/** The metrics admin-service snapshots. Stable strings, used as map keys. */
export type HomeMetricName =
  | "users.total"
  | "content.total"
  | "moderation.pending"
  | "content.flags.total"
  | "judge.decisions.total";

export interface HomeMetricDelta {
  metric: HomeMetricName;
  /** Live count, read at request time. Null when that count could not be taken. */
  current: number | null;
  /**
   * The newest snapshot in the seven days ending a week ago. Null when nothing
   * was recorded in that window.
   */
  weekAgo: number | null;
  /**
   * The day `weekAgo` was captured for, `YYYY-MM-DD`. Absent when `weekAgo` is
   * null. It is NOT always exactly seven days back, which is why it is sent:
   * the baseline is the newest snapshot in a window, so a gap in the series
   * degrades it to a slightly older day rather than to nothing.
   */
  weekAgoDate?: string;
  /** The equivalent a week further back, for period-on-period comparison. */
  priorWeek: number | null;
  priorWeekDate?: string;
}

export interface HomeDeltas {
  /** The UTC day the windows were measured back from, `YYYY-MM-DD`. */
  asOf: string;
  metrics: HomeMetricDelta[];
}

export function getHomeDeltas(): Promise<HomeDeltas> {
  return httpRequest<HomeDeltas>("/v1/admin/home/deltas");
}

// --- Entity assets (ADR-036) -------------------------------------------------
//
// Two independent planes behind one page, and they must not be conflated: text
// overrides are game-service's, bespoke imagery is content-service's. The
// override messages carry no image field, and the asset routes carry no text.
//
// There is no imagery READ here. Bespoke art resolves deterministically from
// the entity id, so the page builds the CDN URL itself and lets a 404 answer
// the question - see lib/entity-assets.ts. Asking the server to probe object
// existence would rebuild the coverage manifest ADR-036 exists to delete.

/**
 * What the entity IS. Drives search and the entity detail read.
 *
 * NOT the artwork address. The asset routes are keyed by ROLE, which is a
 * different vocabulary with a different cardinality: a player has two roles and
 * a hero belongs to two kinds. See EntityAssetRoleValue.
 */
export type EntityKindValue = "crest" | "competition" | "player" | "manager" | "stadium";

/**
 * Which image of an entity a request addresses, and the canonical URL segment
 * for it.
 *
 * The v1 segments (competition, player, manager, stadium) are still accepted
 * server-side as aliases so the previously deployed page kept working, but
 * nothing here sends them any more. Two of them pointed at retired directories.
 */
export type EntityAssetRoleValue = "crest" | "badge" | "avatar" | "hero" | "image";

/**
 * One entity as identity-server describes it. The name here is what readers
 * see: identity applies the admin override before answering a lookup.
 */
export interface EntityFacts {
  entityId: string;
  type: EntityKindValue;
  name: string;
  biography: string;
  /**
   * identity's own image reference, usually a relative R2 key. NOT the bespoke
   * BTL asset. Render it only through the resolver's BTL-CDN safety check -
   * a raw provider hotlink must never be rendered from an admin surface.
   */
  imageUrl: string;
  /** The attribution that travels with imageUrl wherever it is shown. */
  imageCredit: string;
}

/** Which of the sources produced the name or biography currently in force. */
export type EntityTextSource = "override" | "baked" | "enrichment" | "none";

/**
 * Everything one screen needs about one entity, in one call.
 *
 * THREE UPSTREAMS, INDEPENDENTLY DEGRADED. identity names the entity,
 * game-service holds the override, and game-service also holds the Wikidata
 * enrichment. Each can be missing on its own, and the two `*Unavailable` flags
 * are how the page says which half it is missing rather than refusing to load.
 *
 * The resolved fields sit beside the raw sources rather than replacing them:
 * deciding whether to write an override means seeing both what is in force and
 * what it is standing on.
 */
export interface EntityDetail {
  entityId: string;
  /** Empty when identity could not name the entity. The page falls back to its own selector. */
  type: EntityKindValue | "";

  displayName: string;
  displayNameSource: EntityTextSource;
  biography: string;
  biographySource: EntityTextSource;

  identity: EntityFacts | null;
  /** identity-server could not be reached. Different from an entity it does not hold. */
  identityUnavailable: boolean;
  identityFound: boolean;
  identityError?: string;

  override: EntityOverride | null;
  /** game-service could not be reached, so the override cannot be written either. */
  overrideUnavailable: boolean;
  overrideError?: string;

  enrichment: { biography: string; imageUrl: string; imageCredit: string } | null;
}

export interface SearchEntitiesResponse {
  results: EntityFacts[];
}

/**
 * The picker's type-ahead, against identity-server's canonical registry.
 *
 * Two limits worth knowing before reading an empty result as a fault. Search
 * matches the BAKED name index, which no admin override rewrites, so an entity
 * renamed by an override is found under its old name. And the deployed
 * artifact holds no venues at all, so a stadium is never a search result. The
 * canonical-id field is the lane for both.
 */
export function searchEntities(params: {
  query: string;
  type?: EntityKindValue;
  limit?: number;
}): Promise<SearchEntitiesResponse> {
  return httpRequest<SearchEntitiesResponse>("/v1/admin/entities/search", {
    params: { q: params.query, type: params.type, limit: params.limit },
  });
}

/** The one read the page makes when an entity is loaded. */
export function getEntityDetail(entityId: string): Promise<EntityDetail> {
  return httpRequest<EntityDetail>(`/v1/admin/entities/${encodeURIComponent(entityId)}`);
}

export interface EntityOverride {
  entityId: string;
  /** Replaces every baked name variant on the entity. Empty means baked names stand. */
  displayName: string;
  /**
   * Replaces the baked biography AND suppresses the Wikidata overlay for this
   * entity, so a correction cannot be undone by the next enrichment fetch.
   */
  biography: string;
  /** Internal editorial rationale. Never rendered to readers. */
  reason: string;
  /** Taken from the verified auth context server-side. Never sent. */
  updatedBy: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListEntityOverridesResponse {
  overrides: EntityOverride[];
  nextPageToken: string;
}

/**
 * Page the override set, optionally narrowed to one entity kind.
 *
 * `kind` is identity's own vocabulary (player, team, coach, competition,
 * venue, season, official, game), not this page's artwork kinds, because an
 * override can be written against anything the registry holds. game-service
 * prefix matches it against the entity id, which is the only thing on an
 * override row that says what kind of entity was corrected.
 *
 * Omit it for every kind. An unrecognised value is a 400, not a silent
 * unfiltered list.
 */
export function listEntityOverrides(
  params: {
    pageSize?: number;
    pageToken?: string;
    kind?: string;
  } = {},
): Promise<ListEntityOverridesResponse> {
  return httpRequest<ListEntityOverridesResponse>("/v1/admin/entity-overrides", {
    params: { pageSize: params.pageSize, pageToken: params.pageToken, kind: params.kind },
  });
}

export interface SetEntityOverrideResponse {
  /** True when both text fields were empty and the row was deleted. */
  cleared: boolean;
  override: EntityOverride | null;
}

/**
 * FULL-ROW write: the stored row is exactly what this sends, so an omitted
 * field is cleared rather than preserved. Sending both text fields empty
 * deletes the override and reverts the entity to its baked facts.
 *
 * reason is OPTIONAL (owner, 2026-09-07: "Not really needed"). The Entity
 * assets form no longer collects one; the field still travels to the server
 * for the audit trail and the Corrections list, which shows it when a caller
 * does supply one.
 */
export function setEntityOverride(
  entityId: string,
  params: { displayName: string; biography: string; reason?: string },
): Promise<SetEntityOverrideResponse> {
  return httpRequest<SetEntityOverrideResponse>(
    `/v1/admin/entity-overrides/${encodeURIComponent(entityId)}`,
    {
      method: "PUT",
      body: JSON.stringify({
        displayName: params.displayName,
        biography: params.biography,
        reason: params.reason ?? "",
      }),
    },
  );
}

/**
 * The role routes. The segment is the ROLE, never the entity kind.
 *
 * Sending the kind is what put a player's artwork request on
 * /entity-assets/player/, which the server still honours as a v1 alias mapped
 * to HERO - so a player upload worked and a player AVATAR was unreachable,
 * because "avatar" has no v1 spelling at all.
 */
function entityAssetPath(role: EntityAssetRoleValue, entityId: string): string {
  return `/v1/admin/entity-assets/${role}/${encodeURIComponent(entityId)}`;
}

export interface EntityAssetUploadURL {
  uploadUrl: string;
  publicUrl: string;
  objectKey: string;
  expiresAt?: string;
}

/**
 * Presign a direct PUT for a RASTER asset. The server refuses SVG here and says
 * so: an SVG has to go through uploadEntityAsset, which sanitises it.
 */
export function createEntityAssetUploadUrl(
  role: EntityAssetRoleValue,
  entityId: string,
  params: { contentType: string; sizeBytes: number },
): Promise<EntityAssetUploadURL> {
  return httpRequest<EntityAssetUploadURL>(`${entityAssetPath(role, entityId)}/upload-url`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface EntityAssetStored {
  publicUrl: string;
  objectKey: string;
  /** Size AFTER sanitisation, which is how the operator sees something was stripped. */
  storedBytes: number;
}

/**
 * Send the bytes through content-service. This is the SVG path: the file is
 * sanitised with a default-drop allowlist before it is stored, which a
 * presigned PUT could not do because it never passes through the service.
 *
 * The body is the file itself and the Content-Type is the file's own, because
 * both the sanitiser and the raster signature check start from what is declared.
 */
export function uploadEntityAsset(
  role: EntityAssetRoleValue,
  entityId: string,
  file: File,
): Promise<EntityAssetStored> {
  return httpRequest<EntityAssetStored>(entityAssetPath(role, entityId), {
    method: "POST",
    headers: { "Content-Type": file.type },
    body: file,
  });
}

export interface DeleteEntityAssetResponse {
  /** Keys actually removed. Empty when there was no bespoke art, which is not an error. */
  deletedKeys: string[];
}

export function deleteEntityAsset(
  role: EntityAssetRoleValue,
  entityId: string,
): Promise<DeleteEntityAssetResponse> {
  return httpRequest<DeleteEntityAssetResponse>(entityAssetPath(role, entityId), {
    method: "DELETE",
  });
}

/**
 * Remove ONE stored object, addressed by the key it is stored under.
 *
 * The route above addresses (role, entityId) and checks the id against the
 * role's prefix rule, which a MIS-FILED object fails by construction: a coach
 * id under the players-only avatar directory is not a pair that route can
 * build. Those objects are exactly what the listing reports, so this mode
 * takes the key the listing gave and sends it verbatim. A caller must never
 * rebuild the key from a row's role and id - on the rows this exists for, the
 * two disagree.
 *
 * Idempotent. A well-formed key with nothing behind it answers 200 with an
 * empty `deletedKeys` rather than a 404, so a stale row is not a failure. A key
 * the server refuses is a 400 carrying the reason.
 */
export function deleteEntityAssetByKey(objectKey: string): Promise<DeleteEntityAssetResponse> {
  return httpRequest<DeleteEntityAssetResponse>("/v1/admin/entity-assets", {
    method: "DELETE",
    params: { objectKey },
  });
}

/** One stored object under media/btl/**, described entirely by its key. */
export interface EntityAssetListing {
  /** Full object key, e.g. media/btl/crest/btl_football_team_42.svg. */
  objectKey: string;
  /** Parsed from the key's filename. Not checked against identity. */
  entityId: string;
  /** Parsed from the key's directory. Always a canonical role segment. */
  role: EntityAssetRoleValue;
  sizeBytes: number;
  updatedAt?: string;
}

export interface ListEntityAssetsResponse {
  assets: EntityAssetListing[];
  nextPageToken: string;
}

/**
 * Page the bespoke art that actually exists, optionally filtered to one role.
 *
 * A DIRECTORY LISTING, not a coverage index. It answers "what art do we have",
 * which nothing could answer before without guessing an id and probing the CDN.
 * It does not answer "does this entity have art" for any entity, including one
 * whose row it just returned - that is still the CDN's own 200/404 and still
 * deterministic from (entityId, role).
 *
 * PAGINATION IS THE TOKEN, NOT THE COUNT. content-service skips keys that do
 * not parse as an entity asset after the object store has already spent them
 * against the page, so a response with zero assets AND a next token is correct
 * and means keep going. A caller that stops on an empty page truncates the
 * listing silently.
 */
export function listEntityAssets(
  params: {
    role?: EntityAssetRoleValue;
    pageSize?: number;
    pageToken?: string;
  } = {},
): Promise<ListEntityAssetsResponse> {
  return httpRequest<ListEntityAssetsResponse>("/v1/admin/entity-assets", {
    params: { role: params.role, pageSize: params.pageSize, pageToken: params.pageToken },
  });
}

// ── Machine entity tags (the review queue) ──────────────────────────────────
//
// btl.content.v1.MachineTagsService proposes canonical entity tags on articles
// with provenance. Anything at or above content-service's auto-apply
// confidence floor (0.80) is written straight to APPLIED; everything below it
// is stored PENDING_REVIEW and is invisible to readers until a human accepts
// it. This is the surface that drains that queue. Without it a queued tag is
// permanently invisible rather than merely delayed.
//
// THE ROUTES BELOW ARE NOT SERVED YET, and that is deliberate rather than an
// oversight. content-service annotates the RPCs onto ITS OWN gateway paths
// (`GET /api/v1/machine-subject-refs`, `POST /api/v1/articles/{article_id}/
// machine-subject-refs/{entity_id}/review`), and this dashboard cannot reach
// them: every request here goes to `runtimeConfig.adminApiUrl`
// (admin-api.breakingthelines.{dev,com}), which is admin-service alone.
// Probed on staging while writing this: `/api/v1/taxonomy/nodes`, the one
// pre-existing call in this file that points at a content-service gateway path,
// returns `{"code":5,"message":"Not Found"}` there today, i.e. the Taxonomy
// page's fetch has never resolved in staging or prod. That is the trap this
// section is written to avoid repeating.
//
// So these two functions name the admin-service route family the queue needs,
// in this file's own convention, and the admin-service PR that adds them
// proxies MachineTagsService with the caller's own verified auth context. That
// is not a detour: ReviewMachineSubjectRef refuses service principals outright
// ("machine tag review is an editorial act"), so the human's identity has to
// survive the hop or the RPC is refused, and routing it through admin-service
// also puts the verdict in the admin audit trail, where an editorial decision
// belongs. Until that lands the page renders its API-error state, which says
// so in as many words.
//
// Shapes below are the JSON projection of the proto messages
// (btl/content/v1/machine_tags.proto, protos#199), camelCase as everything
// else in this file is.

/** btl.content.v1.MachineTagStatus, JSON (string) form. */
export type MachineTagStatusValue =
  | "MACHINE_TAG_STATUS_UNSPECIFIED"
  | "MACHINE_TAG_STATUS_APPLIED"
  | "MACHINE_TAG_STATUS_PENDING_REVIEW"
  | "MACHINE_TAG_STATUS_REJECTED";

/**
 * btl.content.v1.MachineTagReviewDecision, JSON (string) form.
 *
 * ACCEPT flips the ref to APPLIED at any confidence, because a human decision is not
 * bound by the auto-apply floor. REJECT keeps the row as an audit record AND
 * as a do-not-re-propose tombstone, which is why there is no delete: rejecting
 * is how a wrong tag is removed, and the tombstone is what stops the tagger
 * proposing it again on the next re-tag.
 */
export type MachineTagDecisionValue =
  | "MACHINE_TAG_REVIEW_DECISION_ACCEPT"
  | "MACHINE_TAG_REVIEW_DECISION_REJECT";

/** btl.context.v1.SubjectRef, the canonical entity the tag points at. */
export interface MachineTagSubject {
  /** Identity canonical id, always `btl_football_<kind>_<suffix>`. */
  id: string;
  /** btl.context.v1.SubjectType, e.g. SUBJECT_TYPE_PLAYER. */
  type?: string;
  sport?: string;
  label?: string;
  slug?: string;
  imageUrl?: string;
  canonicalUrl?: string;
}

/**
 * One machine-proposed entity tag on one article.
 *
 * `articleId` + `subject.id` is the natural key; there is no row id, so that
 * pair is what the review call takes and what this UI keys rows by.
 */
export interface MachineSubjectRef {
  articleId: string;
  subject?: MachineTagSubject;
  /** Always "machine". Stamped server-side, ignored on write. */
  source?: string;
  /** Provider-qualified, plus prompt version: `provider:model#prompt-vN`. */
  model?: string;
  /** Confirmer confidence in [0,1]. */
  confidence?: number;
  /** Short span quoted from the article text by the confirmer. */
  evidence?: string;
  status?: MachineTagStatusValue;
  taggedAt?: string;
  reviewedAt?: string;
  reviewerId?: string;
}

export interface ListMachineTagsResponse {
  refs: MachineSubjectRef[];
  pageInfo?: PageInfo;
}

export interface ListMachineTagsParams {
  /** Omit to read one status across every article, which is the queue. */
  articleId?: string;
  status?: MachineTagStatusValue;
  pageSize?: number;
  /**
   * content-service's page token is a stringified OFFSET, so a page number
   * maps onto it directly: `(page - 1) * pageSize`. That is what lets this
   * queue use the numbered PaginationFooter every other list page here uses
   * rather than a one-way next/prev cursor.
   */
  pageToken?: string;
}

export function listMachineTags({
  articleId,
  status,
  pageSize,
  pageToken,
}: ListMachineTagsParams = {}): Promise<ListMachineTagsResponse> {
  return httpRequest<ListMachineTagsResponse>("/v1/admin/machine-tags", {
    params: {
      article_id: articleId,
      status,
      page_size: pageSize,
      page_token: pageToken || undefined,
    },
  });
}

export interface ReviewMachineTagParams {
  articleId: string;
  /** The ref's canonical entity id, i.e. `subject.id`, not a row id. */
  entityId: string;
  decision: MachineTagDecisionValue;
}

export interface ReviewMachineTagResponse {
  ref: MachineSubjectRef;
}

/**
 * Record one editorial verdict on one pending ref.
 *
 * HUMANS ONLY, enforced at content-service: a service principal, the tagging
 * operator included, is refused with PermissionDenied before the editorial
 * gate is even consulted, so the tagger can never accept its own proposals.
 * A 401/403 from this call therefore means the session, not the tag; the queue
 * surfaces it as such (see isAuthFailure).
 */
export function reviewMachineTag({
  articleId,
  entityId,
  decision,
}: ReviewMachineTagParams): Promise<ReviewMachineTagResponse> {
  return httpRequest<ReviewMachineTagResponse>(
    `/v1/admin/machine-tags/${encodeURIComponent(articleId)}/${encodeURIComponent(entityId)}/review`,
    {
      method: "POST",
      body: JSON.stringify({ decision }),
    },
  );
}

// =============================================================================
// ARCHETYPES
// =============================================================================
//
// The archetype library (profile-spec.md §5) through admin-service's BFF
// (admin-service#90) over game-service's archetype admin RPCs. game-service
// owns every rule about a definition: registry keys, weights above 0, one
// draft and one published version per archetype, and the revision check. The
// BFF sends enums as lowercase words, which is the spelling used here.

/** Position groups an archetype can name as home, in display order. */
export const ARCHETYPE_HOME_POSITIONS = [
  "centre_back",
  "full_back",
  "defensive_midfielder",
  "central_midfielder",
  "attacking_midfielder",
  "winger",
  "striker",
] as const;

export type ArchetypeHomePosition = (typeof ARCHETYPE_HOME_POSITIONS)[number];

/**
 * A metric's data class. `event` needs licensed event data; `match_stats`
 * exists on the wire but game-service refuses it as an archetype's own class.
 */
export type MetricDataClass = "season_stats" | "match_stats" | "event" | "";

export type ArchetypeStatus = "draft" | "published" | "retired" | "";

/** One row of the library list. */
export interface ArchetypeSummary {
  archetypeId: string;
  name: string;
  homePositions: string[];
  requiredData: MetricDataClass;
  /** The status of the version readers see: published, else draft, else retired. */
  status: ArchetypeStatus;
  version: number;
  /** 0 when nothing is published. */
  publishedVersion: number;
  /** 0 when there is no draft. */
  draftVersion: number;
  displayOrder: number;
  /** Latest creation or publish time over every version. */
  updatedAt?: string | null;
}

export interface ArchetypeWeight {
  metricKey: string;
  /** The registry's label, "" when the registry no longer knows the key. */
  label: string;
  weight: number;
  /** Derived by game-service from the registry, never sent by a client. */
  requiredData: MetricDataClass;
}

/** One version of an archetype, as an admin sees it. */
export interface ArchetypeVersion {
  archetypeId: string;
  version: number;
  name: string;
  homePositions: string[];
  requiredData: MetricDataClass;
  positional: boolean;
  weights: ArchetypeWeight[];
  status: ArchetypeStatus;
  displayOrder: number;
  /** The draft's optimistic-concurrency token. */
  revision: number;
  notes: string;
  createdById?: string;
  createdByName?: string;
  createdAt?: string | null;
  publishedById?: string;
  publishedByName?: string;
  publishedAt?: string | null;
}

export interface ArchetypeDetail {
  archetypeId: string;
  draft: ArchetypeVersion | null;
  published: ArchetypeVersion | null;
  /** Every version, newest first. */
  versions: ArchetypeVersion[];
}

export function listArchetypes(): Promise<{ archetypes: ArchetypeSummary[] }> {
  return httpRequest<{ archetypes: ArchetypeSummary[] }>("/v1/admin/archetypes");
}

/** 404 when the id has no versions. */
export function getArchetype(archetypeId: string): Promise<ArchetypeDetail> {
  return httpRequest<ArchetypeDetail>(`/v1/admin/archetypes/${encodeURIComponent(archetypeId)}`);
}

/**
 * The definition an admin edits. It is the save body, and the shape the
 * preview (PF-A2b) scores, less the revision.
 */
export interface ArchetypeDefinition {
  name: string;
  homePositions: string[];
  weights: { metricKey: string; weight: number }[];
  /** `event` for an event-only archetype, else `season_stats`. Always sent. */
  requiredData: "season_stats" | "event";
  /** Always sent: a save replaces the whole draft. */
  positional: boolean;
  notes: string;
}

export interface SaveArchetypeDraftParams extends ArchetypeDefinition {
  archetypeId: string;
  /** The draft's revision as last read, 0 when there is no draft. Stale is 409. */
  expectedRevision: number;
}

/** Creates or updates the single draft. An unknown id creates the archetype. */
export function saveArchetypeDraft({
  archetypeId,
  expectedRevision,
  ...definition
}: SaveArchetypeDraftParams): Promise<{ draft: ArchetypeVersion }> {
  return httpRequest<{ draft: ArchetypeVersion }>(
    `/v1/admin/archetypes/${encodeURIComponent(archetypeId)}/draft`,
    {
      method: "PUT",
      body: JSON.stringify({ ...definition, expectedRevision }),
    },
  );
}

export interface PublishArchetypeParams {
  archetypeId: string;
  /** The draft version being published. */
  version: number;
  /** The draft's revision as last read. Stale is 409. */
  expectedRevision: number;
}

/** Publishes the draft and retires the previously published version. */
export function publishArchetype({
  archetypeId,
  version,
  expectedRevision,
}: PublishArchetypeParams): Promise<{ published: ArchetypeVersion; retiredVersion: number }> {
  return httpRequest<{ published: ArchetypeVersion; retiredVersion: number }>(
    `/v1/admin/archetypes/${encodeURIComponent(archetypeId)}/publish`,
    {
      method: "POST",
      body: JSON.stringify({ version, expectedRevision }),
    },
  );
}

/** Retires the published version. 404 when nothing is published. */
export function retireArchetype(archetypeId: string): Promise<{ retired: ArchetypeVersion }> {
  return httpRequest<{ retired: ArchetypeVersion }>(
    `/v1/admin/archetypes/${encodeURIComponent(archetypeId)}/retire`,
    { method: "POST" },
  );
}

export interface MetricDefinition {
  key: string;
  label: string;
  shortLabel?: string;
  family: string;
  unit: "per_90" | "percent" | "per_match" | "";
  lowerIsBetter: boolean;
  dataClass: MetricDataClass;
  order: number;
}

export interface MetricTemplate {
  entityType: string;
  templateId: string;
  methodVersion: string;
  families: string[];
  metrics: MetricDefinition[];
}

/** The metric registry. 503 while game-service's profile reads are off. */
export function listMetricTemplates(
  entityType: "player" | "team" | "manager",
): Promise<{ templates: MetricTemplate[] }> {
  return httpRequest<{ templates: MetricTemplate[] }>("/v1/admin/metric-templates", {
    params: { entityType },
  });
}

// --- Archetype preview (PF-A2b) ----------------------------------------------

export interface ArchetypePreviewEntity {
  id: string;
  /** Resolved by the BFF from identity, best effort. Absent when the lookup missed. */
  name?: string;
}

export interface ArchetypePreviewEntry {
  player: ArchetypePreviewEntity;
  team: ArchetypePreviewEntity | null;
  fitPercentile: number;
  score: number;
}

export interface ArchetypeOverlap {
  archetypeId: string;
  name: string;
  /** Rank correlation of fit scores over the same pool. */
  spearman: number;
  /** game-service's verdict: above 0.8. */
  flagged: boolean;
}

/** One preview. Empty (no top, homeSize 0) until the season's metrics exist. */
export interface ArchetypePreview {
  top: ArchetypePreviewEntry[];
  homeSize: number;
  overlaps: ArchetypeOverlap[];
}

export interface PreviewArchetypeParams {
  /** The archetype being edited, left out of the overlap check. "" for a new one. */
  archetypeId: string;
  homePositions: string[];
  weights: { metricKey: string; weight: number }[];
  requiredData: "season_stats" | "event";
  competitionId: string;
  seasonId: string;
}

/**
 * Scores an unsaved definition over one competition season. A POST that
 * changes nothing; admin-service does not audit it.
 */
export function previewArchetype(params: PreviewArchetypeParams): Promise<ArchetypePreview> {
  return httpRequest<ArchetypePreview>("/v1/admin/archetypes/preview", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export interface CompetitionSeason {
  /** The competition's own season id, the id the preview takes. */
  id: string;
  label: string;
}

export interface CompetitionSeasons {
  competition: ArchetypePreviewEntity;
  /** The season the competition's table is on now. "" when there is none. */
  currentSeasonId: string;
  /** Most recent first. */
  seasons: CompetitionSeason[];
}

/** One competition's seasons (admin-service PF-A2b, over GetCompetitionPage). */
export function listCompetitionSeasons(competitionId: string): Promise<CompetitionSeasons> {
  return httpRequest<CompetitionSeasons>(
    `/v1/admin/competitions/${encodeURIComponent(competitionId)}/seasons`,
  );
}
